import React from "react";
import { GetStaffFilters } from "@/services/staffService";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTeams } from "../hooks/useTeams";
import { X } from "lucide-react";
import { UserMultiSelect } from "./UserMultiSelect";
import { TeamMultiSelect } from "./TeamMultiSelect";
import { useStaffPositions } from "../hooks/useStaff";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import TruncatedText from "@/components/common/TruncatedText";
import { useTranslation } from "react-i18next";
import { FilterSingleSelect } from "@/components/common/FilterSingleSelect";

type StaffStatusFilter = "all" | "on-site" | "not-on-site";

interface StaffFiltersProps {
  filters: GetStaffFilters;
  onFilterChange: (filters: GetStaffFilters) => void;
  statusFilter: StaffStatusFilter;
  onStatusFilterChange: (value: StaffStatusFilter) => void;
  teamOnShiftCounts: Record<string, number>;
}

export const StaffFilters: React.FC<StaffFiltersProps> = ({
  filters,
  onFilterChange,
  statusFilter,
  onStatusFilterChange,
  teamOnShiftCounts,
}) => {
  const { t } = useTranslation();
  const hotelId = useCurrentHotelId();
  const { data: teamsData } = useTeams({
    isActive: true,
    sortBy: null,
    sortOrder: null,
  });
  const { data: positions } = useStaffPositions(hotelId ?? "");
  const { data: teams } = teamsData || {};

  const handleMinTasksChange = (value: string) => {
    const minTasks = value === "all" ? undefined : parseInt(value, 10);
    onFilterChange({ ...filters, minTasks, currentPage: 1 });
  };

  const handleStatusChange = (value: string) => {
    if (value === "all" || value === "on-site" || value === "not-on-site") {
      onStatusFilterChange(value);
    }
  };

  const handlePositionChange = (value: string) => {
    const position = value === "all" ? undefined : value;
    onFilterChange({ ...filters, position });
  };

  const handleTeamToggle = (teamId: string) => {
    if (filters.teamId === teamId) {
      // Deselect if already selected
      onFilterChange({ ...filters, teamId: undefined, currentPage: 1 });
    } else {
      // Select this team
      onFilterChange({ ...filters, teamId, currentPage: 1 });
    }
  };

  const handleRoleChange = () => undefined;

  const statusOptions = [
    { value: "all", label: t("staff.filters.all") },
    { value: "on-site", label: t("staff.filters.onSite") },
    { value: "not-on-site", label: t("staff.filters.notOnSite") },
  ];
  const positionOptions = [
    { value: "all", label: t("staff.filters.allPositions") },
    ...(positions?.map((position) => ({
      value: position,
      label: position,
    })) ?? []),
  ];
  const roleOptions = [
    { value: "staff", label: t("staff.table.staff", "Staff") },
  ];
  const taskOptions = [
    { value: "all", label: t("staff.filters.anyTasks") },
    { value: "0", label: t("staff.filters.tasksOrMore", { count: 0 }) },
    { value: "5", label: t("staff.filters.tasksOrMore", { count: 5 }) },
    { value: "10", label: t("staff.filters.tasksOrMore", { count: 10 }) },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Dropdowns */}
      <div className="flex flex-wrap items-center gap-4">
        <FilterSingleSelect
          className="w-[120px] justify-between"
          options={statusOptions}
          value={statusFilter}
          onValueChange={handleStatusChange}
          placeholder={t("staff.filters.status")}
        />

        <UserMultiSelect
          selectedUserIds={filters.userIds || []}
          onSelectionChange={(userIds) =>
            onFilterChange({
              ...filters,
              userIds,
              currentPage: 1,
            })
          }
        />

        <FilterSingleSelect
          className="w-[140px] justify-between"
          options={positionOptions}
          value={filters.position ?? "all"}
          onValueChange={handlePositionChange}
          placeholder={t("staff.filters.position")}
        />

        <FilterSingleSelect
          className="w-[120px] justify-between"
          options={roleOptions}
          value="staff"
          onValueChange={handleRoleChange}
          placeholder={t("staff.filters.role")}
        />

        <TeamMultiSelect
          selectedTeamIds={filters.teamIds || []}
          onSelectionChange={(teamIds) =>
            onFilterChange({
              ...filters,
              teamIds,
              currentPage: 1,
            })
          }
        />

        <FilterSingleSelect
          className="w-[200px] justify-between"
          options={taskOptions}
          value={filters.minTasks?.toString() || "all"}
          onValueChange={handleMinTasksChange}
          placeholder={t("staff.filters.byTasks")}
        />
      </div>

      {/* Team Badge Chips */}
      {teams && teams.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {teams.map((team) => {
            const isSelected = filters.teamId === team.id;
            const count = teamOnShiftCounts[team.id] || 0;
            return (
              <Button
                type="button"
                key={team.id}
                variant={isSelected ? "new_secondary" : "new_outline"}
                size="sm"
                onClick={() => handleTeamToggle(team.id)}
              >
                <Badge
                  variant={count > 0 ? "number_default" : "number_outline"}
                >
                  {count}
                </Badge>
                <TruncatedText limit={30}>{team.name}</TruncatedText>
                {isSelected ? <X /> : null}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};
